/**
 * The Layout (Overlay / Grid) switch - the shared `<layout-select>` - shown under the ship selector once more than one
 * ship is plotted.
 *
 * Ship selection itself lives in `<ship-multi-select>` - one control for
 * "which ship" and "which ships to compare", with the chips beneath it.
 *
 * This is layout only: `setViewMode` redraws from cached data with no refetch,
 * which is why it can sit outside the Plot button's request path.
 *
 * Plot tab only, and historical only - Live is single-ship by design.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { supportsGrid, supportsOverlay } from './multi-ship-plot.ts';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeTab,
  selectedShip,
  comparisonShips,
  activeDataSource,
  plottedShipIds,
} from '../../state/store.ts';
import { plotState, setGaugeLayout, setViewMode } from '../../state/plot-state.ts';
import { GAUGE_LAYOUTS } from './gauge-config.ts';
import '../../ui/layout-select.ts';
import type { ChartLayout } from '../../ui/layout-select.ts';

@customElement('plot-ship-picker')
export class PlotShipPicker extends LightElement {
  private store = new StoreController(this, [
    activeTab,
    selectedShip,
    comparisonShips,
    activeDataSource,
    plotState,
  ]);

  /**
   * A gauge lays its ships out itself, so its choice is how: a panel per ship,
   * a panel per dial, or a matrix. Same control and keyboard as Overlay/Grid.
   */
  private renderGaugeLayout(): TemplateResult {
    const current = plotState.value.gauge_layout;
    const onKey = (e: KeyboardEvent): void => {
      const i = GAUGE_LAYOUTS.findIndex((l) => l.value === current);
      const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = GAUGE_LAYOUTS[(i + step + GAUGE_LAYOUTS.length) % GAUGE_LAYOUTS.length]!.value;
      setGaugeLayout(next);
      void this.updateComplete.then(() => this.querySelector<HTMLElement>(`[data-layout="${next}"]`)?.focus());
    };
    return html`<div class="axis-block layout-select">
      <label>Layout</label>
      <div class="seg" role="radiogroup" aria-label="Gauge layout" @keydown=${onKey}>
        ${GAUGE_LAYOUTS.map(({ value, label, title }) => html`<button
          type="button"
          class="seg-btn ${current === value ? 'active' : ''}"
          role="radio"
          data-layout=${value}
          aria-checked=${current === value ? 'true' : 'false'}
          tabindex=${current === value ? 0 : -1}
          title=${title}
          @click=${() => setGaugeLayout(value)}
        >${label}</button>`)}
      </div>
    </div>`;
  }

  private renderViewMode(): TemplateResult {
    const s = plotState.value;
    if (s.plot_type === 'gauge') return this.renderGaugeLayout();
    // A heatmap is a density field: two of them stacked read as neither, so it
    // draws as panels whatever the switch says. Shown disabled rather than
    // hidden, so the reason is visible where the choice would be.
    const noOverlay = supportsGrid(s.plot_type) && !supportsOverlay(s.plot_type);
    return html`<layout-select
      .value=${s.view_mode}
      .overlayUnavailable=${noOverlay ? `A ${s.plot_type} cannot be overlaid - one panel per ship` : null}
      .select=${(mode: ChartLayout) => setViewMode(mode)}
    ></layout-select>`;
  }

  override render(): TemplateResult {
    // Export ignores comparison ships entirely.
    if (activeTab.value !== 'plot') return html`${nothing}`;
    // Ship selection and its chips live in <ship-multi-select> above; only the
    // layout switch is left here, and only once there is more than one ship.
    if (plottedShipIds().length < 2) return html`${nothing}`;
    // Ships' tables are always one table with a Ship column: no layout to pick.
    if (plotState.value.plot_type === 'table') return html`${nothing}`;
    return html`
      <div class="plot-ships">${this.renderViewMode()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plot-ship-picker': PlotShipPicker;
  }
}
